import { marked } from 'marked';
import { Resource, ResourceType } from '../types/resource';

export const getRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const chatDate = new Date(date);
  
  // Handle invalid dates
  if (isNaN(chatDate.getTime())) {
    return 'Unknown date';
  }

  // Reset hours to compare just dates
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateToCompare = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());
  
  const diffTime = today.getTime() - dateToCompare.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Handle future dates
  if (diffDays < 0) {
    return chatDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  // Handle relative times
  switch (true) {
    case diffDays === 0:
      return 'Today';
    case diffDays === 1:
      return 'Yesterday';
    case diffDays < 7:
      return `${diffDays} days ago`;
    case diffDays < 30:
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    default:
      return chatDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
  }
};

export const copyRichText = async (text: string): Promise<void> => {
  const cleanMarkdown = stripMessageContent(text);

  // Create temporary container for HTML conversion
  const tempDiv = document.createElement('div');
  
  // Convert markdown to HTML
  const parsedContent = marked.parse(cleanMarkdown, {
    gfm: true,
    breaks: true
  });
  
  // Handle both string and Promise<string> return types
  if (parsedContent instanceof Promise) {
    tempDiv.innerHTML = await parsedContent;
  } else {
    tempDiv.innerHTML = parsedContent;
  }

  // Style the HTML content
  const styledHTML = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;">
      ${tempDiv.innerHTML}
    </div>
  `.replace(/<pre>/g, '<pre style="background-color: #f6f8fa; padding: 16px; border-radius: 6px; overflow: auto;">');

  // Create a Blob with HTML content
  const blob = new Blob([styledHTML], { type: 'text/html' });
  
  // Create the clipboard data
  const clipboardData = new ClipboardItem({
    'text/plain': new Blob([cleanMarkdown], { type: 'text/plain' }),
    'text/html': blob
  });

    // Copy to clipboard using the new Clipboard API
    navigator.clipboard.write([clipboardData]).then(() => {
    }).catch(err => {
      // Fallback to basic text copying if rich copy fails
      navigator.clipboard.writeText(cleanMarkdown).then(() => {
      }).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    });
}

// Rule of thumb from OpenAI's tokenizer docs: for common English text one
// token ≈ 4 characters (≈ ¾ of a word, so 100 tokens ≈ 75 words). We use this
// as the single estimator throughout the app — it's cheap, deterministic, and
// close enough for budgeting/capacity checks without shipping a full BPE
// tokenizer to the browser.
export const CHARS_PER_TOKEN = 4;

export const estimateTokensFromChars = (charCount: number): number => {
  if (!Number.isFinite(charCount) || charCount <= 0) return 0;
  return Math.ceil(charCount / CHARS_PER_TOKEN);
};

export const estimateTokensFromText = (text: string | null | undefined): number => {
  if (!text) return 0;
  return estimateTokensFromChars(text.length);
};

export const getTokenCount = (text: string): number => estimateTokensFromText(text);

export const getActualTokenCount = (text: string): number => estimateTokensFromText(text);

export const parseTextContent = (text: string, chatId: string): Resource[] => {
  if (!text) return [];

  const resources: Resource[] = [];
  const sections = text.split(/(?=<(?:PDF|DOC|CODE) START>)/);

  sections.forEach((section, index) => {
    if (!section.trim()) return;

    const typeMatch = section.match(/<(PDF|DOC|CODE) START>/);
    const titleMatch = section.match(/\[(?:PDF|DOC|CODE) TITLE:\s*(.*?)\]/);
    const linkMatch = section.match(/\[LINK:\s*(.*?)\]/);

    if (!typeMatch || !titleMatch) return;

    const docType = typeMatch[1].toLowerCase();
    const title = titleMatch[1].trim() || `Untitled ${docType.toUpperCase()} ${index + 1}`;
    const url = linkMatch ? linkMatch[1].trim() : null;

    const contentMatch = section.match(/.*?\]\s*([\s\S]*?)\s*<(?:PDF|DOC|CODE) END>/s);
    if (!contentMatch) return;

    const languageMatch = section.match(/\[LANGUAGE:\s*(.*?)\]/);
    const language = languageMatch ? languageMatch[1].trim() : null;

    let content = contentMatch[1].trim();
    if (!content) return;

    // Remove the link block if present
    content = content.replace(/\[LINK:\s*.*?\]/, '').trim();
    
    // Remove the language block if present
    if (languageMatch) {
      content = content.replace(/\[LANGUAGE:\s*.*?\]/, '').trim();
    }

    const resourceId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const resource: Resource = {
      id: resourceId,
      title,
      content,
      type: docType as ResourceType,
      url: url || undefined,
      timestamp: new Date().toISOString(),
      chatId: chatId,
      language: language || undefined
    };

    resources.push(resource);
  });

  return resources;
};

export const formatPastedText = (text: string): string => {
  const firstLine = text.split('\n')[0].trim().slice(0, 10); // Get first 10 chars
  const title = `Pasted: ${firstLine}...`;
  
  const formattedText = `<PDF START>\n[PDF TITLE: ${title}]\n[LINK: ] [Pasted] \n${text}\n<PDF END>\n\n`;
  return formattedText;
};

interface ThinkingContent {
  time: number | null;
  content: string | null;
}

export const parseThinkingContent = (content: string | null): ThinkingContent => {
  if (!content) return { time: null, content: null };

  // Extract time and content from the formatted string
  const timeMatch = content.match(/\[Time: ([\d.]+)s\]/);
  const contentMatch = content.match(/\[Content: ([\s\S]*?)\]/);
  
  const time = timeMatch ? parseFloat(timeMatch[1]) : null;
  const thinkingContent = contentMatch ? contentMatch[1].trim() : null;
  
  return { time, content: thinkingContent };
}
interface HeaderResponse {
  greeting: string;
  subtext: string;
}

export const getRandomHeader = (_userName: string): HeaderResponse => {
  const hour = new Date().getHours();

  // Shortened, time-specific greetings.
  let timeBasedGreetings: string[];
  if (hour < 12) {
    timeBasedGreetings = [
      "How can I help you this morning?"
    ];
  } else if (hour >= 12 && hour < 18) {
    timeBasedGreetings = [
      //"Hope your day's going well",
      "How can I help you this afternoon?"
    ];
  } else {
    timeBasedGreetings = [
      //"Hope you had a good day",
      "How can I help you this evening?"
    ];
  }

  const selectedGreeting = timeBasedGreetings[Math.floor(Math.random() * timeBasedGreetings.length)] || "Hello there";

  // Subtext randomization (drawn from original subtexts)
  const subtexts = [
    "What shall we explore together?",
    "Ready to learn something new?",
    "What questions do you have for me?",
    "How can I help your learning goals?",
    "What interests you the most?",
    "Let's tackle something!",
    "I'm here to help you understand",
    "Let's learn together!",
    "What's on your mind?",
    "How can I assist you",
    "What interests you?",
    "Let's get started!",
    "What problems can we solve together?",
    "Let's explore new ideas and concepts.",
    "What's your goal for today?",
    "What sparks your curiosity?",
    "How can I guide you?",
    "Let's learn something new.",
    "Where shall we start?",
    "What is on your mind?",
    "Let's begin learning!",
    "Let's learn something new."
  ];
  const subtext = subtexts[Math.floor(Math.random() * subtexts.length)];

  return {
    greeting: selectedGreeting,
    subtext
  };
};

export const cleanCodeTags = (input: string): string => {
  const startTag = "<CODE START>";
  const endTag = "<CODE END>";
  const startIdx = input.indexOf(startTag);
  const endIdx = input.indexOf(endTag);

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const contentStart = input.indexOf('\n', startIdx) + 1;
    const contentEnd = endIdx;
    let codeBlock = input.slice(contentStart, contentEnd).trim();
    codeBlock = codeBlock.replace(/^\[CODE TITLE:.*\]\s*/m, '');
    codeBlock = codeBlock.replace(/^\[LANGUAGE:.*\]\s*/m, '');
    // Remove any console.log or similar log statements
    codeBlock = codeBlock.replace(/^\s*console\.(log|error|warn|info|debug)\s*\(.*\);\s*$/gm, '');
    return codeBlock.trim();
  }
  return input;
}

export const cleanMathText = (input: string): string => {
  return input
    // Fix subscripts and superscripts
    .replace(/([_^])(\s+)(\w)/g, '$1$3')
    
    // Clean up fractions
    .replace(/\\frac\s*{/g, '\\frac{')
    .replace(/}\s*{/g, '}{')
    
    // Fix spacing around mathematical operators
    .replace(/\s*([+\-=×÷<>≤≥])\s*/g, ' $1 ')
    
    // Clean up summation and integral notation
    .replace(/\\sum\s*_/g, '\\sum_')
    .replace(/\\int\s*_/g, '\\int_')
    
    // Fix spacing in limits
    .replace(/\\lim\s*_/g, '\\lim_')
    
    // Clean up matrix notation
    .replace(/\\begin\s*{matrix}/g, '\\begin{matrix}')
    .replace(/\\end\s*{matrix}/g, '\\end{matrix}')
    
    // Fix multiple spaces between terms
    .replace(/\s+/g, ' ')
    
    // Clean up parentheses and brackets
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .replace(/\[\s+/g, '[')
    .replace(/\s+\]/g, ']')
    
    // Fix spacing around commas in mathematical expressions
    .replace(/\s*,\s*/g, ', ')
    
    // Clean up Greek letters
    .replace(/\\([A-Za-z]+)\s+/g, '\\$1 ')
    
    // Remove zero-width spaces and other invisible characters
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    
    // Properly space dots in ellipsis
    .replace(/\.\s*\.\s*\./g, '...')
    
    // Clean up infinity symbol
    .replace(/\\infty\s+/g, '\\infty ')
    
    // Fix spacing in numbered equations
    .replace(/\\label\s*{/g, '\\label{')
    
    // Ensure proper spacing around arrow symbols
    .replace(/\\rightarrow\s+/g, '\\rightarrow ')
    .replace(/\\leftarrow\s+/g, '\\leftarrow ')
    
    // Proper handling of derivative notation
    .replace(/\\frac\s*d/g, '\\frac{d')
    
    // Fix spacing in set notation
    .replace(/\\in\s+/g, '\\in ')
    .replace(/\\subset\s+/g, '\\subset ')
    
    // Clean up double line breaks
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    
    .trim();
}

// Added new helper function to return relative time in a compact format (e.g., 1d, 2d, 3m, etc.)
export const getCompactRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const pastDate = new Date(date);
  if (isNaN(pastDate.getTime())) {
    return 'Unknown';
  }

  const diff = now.getTime() - pastDate.getTime();

  // Convert to seconds
  const seconds = Math.floor(diff / 1000);
  
  // Handle very recent messages
  if (seconds < 60) return 'now';

  // Handle minutes
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm';

  // Handle hours
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) return hours + 'h';

  // Handle days
  const days = Math.floor(seconds / 86400);
  if (days < 30) return days + 'd';

  // Handle months
  const months = Math.floor(days / 30);
  if (months < 12) return months + 'mo';

  // Handle years
  const years = Math.floor(months / 12);
  return years + 'y';
};

export const stripMessageContent = (text: string): string => {
  // Only remove the artifact tags, keep all content
  let sanitized = text;

  // Remove resources tags and their content
  sanitized = sanitized.replace(/<resources>[\s\S]*?<\/resources>/g, '');
  
  // Remove think tags and their content
  sanitized = sanitized.replace(/<think>[\s\S]*?<\/think>/g, '');

  // Citations are rendered as interactive source pills, not message text.
  // Exclude their URL payloads from plain-text and rich-text copies, including
  // an unfinished citation at the end of a streaming response.
  sanitized = sanitized.replace(/<citation\b[^>]*>[\s\S]*?<\/citation>/gi, '');
  sanitized = sanitized.replace(/<citation\b[^>]*>[\s\S]*$/gi, '');
  
  // Remove artifact/resource tags but preserve their content
  sanitized = sanitized.replace(/<artifacts>/g, '');
  sanitized = sanitized.replace(/<\/artifacts>/g, '');
  sanitized = sanitized.replace(/<resource>/g, '');
  sanitized = sanitized.replace(/<\/resource>/g, '');
  
  // Remove title tags but preserve content
  sanitized = sanitized.replace(/<title>/g, '');
  sanitized = sanitized.replace(/<\/title>/g, '');
  
  // Remove essay tags but preserve content
  sanitized = sanitized.replace(/<essay>/g, '');
  sanitized = sanitized.replace(/<\/essay>/g, '');
  
  // Remove code tags with attributes but preserve content
  sanitized = sanitized.replace(/<code[^>]*>/g, '');
  sanitized = sanitized.replace(/<\/code>/g, '');
  
  // Remove visualization tags but preserve content
  sanitized = sanitized.replace(/<visualization[^>]*>/g, '');
  sanitized = sanitized.replace(/<\/visualization>/g, '');
  
  // Remove content tags but preserve content
  sanitized = sanitized.replace(/<content>/g, '');
  sanitized = sanitized.replace(/<\/content>/g, '');
  
  // Remove PDF title markers
  sanitized = sanitized.replace(/\[PDF TITLE: .*?\]/g, '');

  // Remove image generation tags
  sanitized = sanitized.replace(/<image_generation>[\s\S]*?<\/image_generation>/g, '');

  // Remove tool action/call tags
  sanitized = sanitized.replace(/<tool_action>[\s\S]*?<\/tool_action>/g, '');
  sanitized = sanitized.replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '');
  sanitized = sanitized.replace(/<tool_action>[\s\S]*$/g, '');
  sanitized = sanitized.replace(/<tool_call>[\s\S]*$/g, '');

  // Remove question cards (and a partial trailing one)
  sanitized = sanitized.replace(/<ask_user\b[^>]*>[\s\S]*?<\/ask_user>/g, '');
  sanitized = sanitized.replace(/<ask_user\b[\s\S]*$/, '');

  // Remove reasoning summary tags
  sanitized = sanitized.replace(/<reasoning_summary>[\s\S]*?<\/reasoning_summary>/g, '');
  
  // remove compose tags (and unclosed trailing block from an interrupted stream)
  sanitized = sanitized.replace(/<compose>[\s\S]*?<\/compose>/g, '');
  sanitized = sanitized.replace(/<compose>[\s\S]*$/, '');

  // remove legacy mail tags (older stored messages), including unclosed trailing block
  sanitized = sanitized.replace(/<mail>[\s\S]*?<\/mail>/g, '');
  sanitized = sanitized.replace(/<mail>[\s\S]*$/, '');

  // Shopping is visual UI; do not copy or speak its internal ids, URLs and
  // prices. Preserve literal shopping markup in Markdown code examples.
  const stripShoppingBlock = (segment: string) => segment
    .replace(/<shopping>[\s\S]*?<\/shopping>/gi, '')
    .replace(/<shopping>[\s\S]*$/gi, '');
  const codePattern = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*(?:`|$)/g;
  let shoppingSafeContent = '';
  let shoppingCursor = 0;
  let codeMatch: RegExpExecArray | null;
  while ((codeMatch = codePattern.exec(sanitized)) !== null) {
    shoppingSafeContent += stripShoppingBlock(sanitized.slice(shoppingCursor, codeMatch.index));
    shoppingSafeContent += codeMatch[0];
    shoppingCursor = codeMatch.index + codeMatch[0].length;
  }
  sanitized = shoppingSafeContent + stripShoppingBlock(sanitized.slice(shoppingCursor));
  
  // Trim any extra whitespace
  return sanitized.trim();
};
